import React, { useState, useMemo, useCallback } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { getResourceNameFromResponseBody } from 'components/services/create/TestEvaluationResultsStatusCheckIntegration'
import { isExternalIntegrationOfType } from 'components/status-checks/authentication-form-validation-utils'
import { useIsDynatraceEnhancedStatusCheck } from 'components/status-checks/hooks/use-is-dynatrace-enhanced-status-check'
import { getIsStandardEndpointResponseValid } from 'components/status-checks/status-checks-form-validation-utils'
import { type DynatracePreFlightSuccessResponse } from 'queries/status-checks/pre-flight/types'
import { type EvaluationResults } from 'types/api/status-checks/evaluation'
import { type EndpointResponse } from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

import type { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'

type EndpointResponseValidation =
  | {
      endpointResponse: DynatracePreFlightSuccessResponse | EndpointResponse
      isStatusCheckConnectionValidated: boolean
    }
  | {
      endpointResponse?: never
      isStatusCheckConnectionValidated: false
    }

type EvaluationResultsValidation =
  | {
      evaluationResults?: EvaluationResults
      isStatusCheckEvaluationValidated: boolean
    }
  | {
      evaluationResults?: never
      isStatusCheckEvaluationValidated: false
    }

export type StatusCheckDetailsContextValues = {
  isTestStatusCheckDisabled: boolean
  isTestSuccessEvaluationDisabled: boolean
  serviceName?: string
  shouldDisplayPredicateConfiguration: boolean
  handleSuccess(
    endpointResponse: EndpointResponse,
    evaluationResults?: EvaluationResults
  ): Promise<void>
  handleTestConnectionError(): void
  resetEndpointEvaluationResponse(): void
  setEndpointResponse(
    endpointResponse:
      | EndpointResponse
      | DynatracePreFlightSuccessResponse
      | undefined
  ): void
  setEvaluationResults(evaluationResults: EvaluationResults | undefined): void
} & EndpointResponseValidation &
  EvaluationResultsValidation

export const StatusCheckDetailsContext = React.createContext<
  StatusCheckDetailsContextValues | undefined
>(undefined)

const fieldsToWatch = [
  'dynatraceSelector',
  'evaluationConfiguration.responseBodyEvaluation.predicates',
  'externalIntegration',
  'statusCheckName'
] as const

const fieldStateNames = [
  'dynatraceSelector.entityId',
  'dynatraceSelector.k8sQuery',
  'evaluationConfiguration.requestTimeout',
  'monitorUrl'
] as const

export const StatusCheckDetailsProvider: React.FC<{
  serviceName?: string
}> = ({ children, serviceName = '' }) => {
  const [endpointResponse, setEndpointResponse] = useState<
    EndpointResponse | DynatracePreFlightSuccessResponse
  >()
  const [evaluationResults, setEvaluationResults] =
    useState<EvaluationResults>()

  const { control, formState, getFieldState, setValue, trigger } =
    useFormContext<StatusCheckFormValues>()

  const [dynatraceSelector, predicates, externalIntegration, statusCheckName] =
    useWatch({ control, name: fieldsToWatch })

  const [
    entityIdFieldState,
    k8sQueryFieldState,
    requestTimeoutFieldState,
    monitorUrlFieldState
  ] = fieldStateNames.map((field) => getFieldState(field, formState))

  const isCustomExternalIntegration = isExternalIntegrationOfType(
    IntegrationType.Custom,
    externalIntegration
  )

  // Field validation flags to ensure 'test status check' action is enabled
  const isDynatraceEnhancedStatusCheck = useIsDynatraceEnhancedStatusCheck()
  const isDynatraceEntityIdValid =
    dynatraceSelector?.entityId && !entityIdFieldState?.error
  const isDynatraceK8sQueryValid =
    dynatraceSelector?.k8sQuery && !k8sQueryFieldState?.error
  const isMonitorUrlFieldValid =
    monitorUrlFieldState?.isDirty && !monitorUrlFieldState?.error

  // When dealing with a Dynatrace enhanced status check, we have additional validation requirements to check.
  const isDynatraceSelectorFieldValid =
    isDynatraceEnhancedStatusCheck &&
    Boolean(isDynatraceEntityIdValid || isDynatraceK8sQueryValid)
  // The endpoint response must also be present for the Dynatrace status check to be considered valid.
  const isDynatraceEnhancedStatusCheckValid =
    isDynatraceSelectorFieldValid && endpointResponse !== undefined

  // We can check if either the Dynatrace selector fields are valid, or the default monitor URL field for all other cases.
  const isTestStatusCheckDisabled =
    !isDynatraceSelectorFieldValid && !isMonitorUrlFieldValid

  const isTestSuccessEvaluationDisabled =
    isTestStatusCheckDisabled ||
    !endpointResponse ||
    Boolean(requestTimeoutFieldState?.error)

  const shouldDisplayPredicateConfiguration = useMemo<boolean>(() => {
    if (!endpointResponse || !('contentType' in endpointResponse))
      return Boolean(predicates?.length)

    const { contentType } = endpointResponse ?? {}
    // We can probably assume here that we lead with '/', but cannot guarantee that this is
    // always placed at the end of the string. Example: PNI responses might be semi-colon delimited.
    const isJsonPattern = /\/json/i
    const isJsonContentType = contentType
      ? isJsonPattern.test(contentType)
      : false

    return isJsonContentType
  }, [endpointResponse, predicates])

  const handleSuccess = useCallback<
    StatusCheckDetailsContextValues['handleSuccess']
  >(
    async (endpointResponse, evaluationResults) => {
      setEndpointResponse(endpointResponse)
      setEvaluationResults(
        (previousValue) => evaluationResults ?? previousValue
      )

      // Pull the resource name from the response body
      const resourceName = externalIntegration
        ? getResourceNameFromResponseBody(endpointResponse, externalIntegration)
        : undefined

      // don't overwrite the status check name from the response body if it exists in the form
      if (resourceName && !statusCheckName) {
        setValue('statusCheckName', resourceName)
        await trigger('statusCheckName')
      }

      // Validate monitorUrl on test success
      await trigger('monitorUrl')
    },
    [externalIntegration, statusCheckName, setValue, trigger]
  )

  const handleTestConnectionError = useCallback(() => {
    setEndpointResponse(undefined)
  }, [])

  const resetEndpointEvaluationResponse = useCallback(() => {
    setEndpointResponse(undefined)
    setEvaluationResults(undefined)
  }, [setEndpointResponse, setEvaluationResults])

  const value = useMemo<StatusCheckDetailsContextValues>(() => {
    const isStatusCheckConnectionValidated =
      isDynatraceEnhancedStatusCheckValid ||
      getIsStandardEndpointResponseValid(endpointResponse)

    return {
      ...(endpointResponse
        ? { endpointResponse, isStatusCheckConnectionValidated }
        : { endpointResponse, isStatusCheckConnectionValidated: false }),
      /*
       * Only restrict evaluation validation checks for Custom integrations. Otherwise we consider
       * the evaluation valid if the connection was validated.
       */
      ...(isCustomExternalIntegration
        ? {
            evaluationResults,
            isStatusCheckEvaluationValidated: Boolean(evaluationResults)
          }
        : { evaluationResults, isStatusCheckEvaluationValidated: true }),
      isTestStatusCheckDisabled,
      isTestSuccessEvaluationDisabled,
      serviceName,
      shouldDisplayPredicateConfiguration,
      handleSuccess,
      handleTestConnectionError,
      resetEndpointEvaluationResponse,
      setEndpointResponse,
      setEvaluationResults
    }
  }, [
    endpointResponse,
    evaluationResults,
    isDynatraceEnhancedStatusCheckValid,
    isCustomExternalIntegration,
    isTestStatusCheckDisabled,
    isTestSuccessEvaluationDisabled,
    serviceName,
    shouldDisplayPredicateConfiguration,
    handleSuccess,
    handleTestConnectionError,
    resetEndpointEvaluationResponse
  ])

  return (
    <StatusCheckDetailsContext.Provider value={value}>
      {children}
    </StatusCheckDetailsContext.Provider>
  )
}

export function useStatusCheckDetailsContext() {
  const context = React.useContext(StatusCheckDetailsContext)

  if (context === undefined) {
    throw new Error(
      'useStatusCheckDetailsContext must be used within a StatusCheckDetailsProvider'
    )
  }

  return context
}
